/**
 * Contains logic for discovering the mapping between WCMS fields and SOLRs fields
 * as well as information about how these content types and fields need to be handled
 */

import utils from '../../common/utils.js';
import config from '../../common/config.js';

// These are core post fields that are always fetched, even if they're not displayed
const CORE_POST_FIELDS = [ 'title', 'url', 'image', 'date', 'content' ];

// Default post field => solr fields for posts across all content types
// Content Type configs can override these defaults
const DEFAULT_FIELDS = {
	title: 'title_txt',
	url: 'permalink',
	image: 'cdc_related_image_str',
	content: 'excerpt_txt',
	date: 'cdc_article_date_dt',
	altLanguages: 'cdc_alternative_languages_*',
	altFormats: 'cdc_alternative_file_formats_*',
	relatedContent: 'cdc_related_pages_*',
};


// Supported WCMS post types, configurations for each
const CONTENT_TYPE_CONFIGS = {
	cdc_page: {
		name: 'Page',
		aliases: ['page', 'CDC Page'],
	},
	image: {
		name: 'Image',
		aliases: ['Picture', 'JPG'],
		fields: {
			title: ['title_txt', 'cdc_alt_text_str'],
			image: 'permalink',
		},
	},
	attachment: {
		name: 'File',
		aliases: ['Attachment', 'DOC', 'PDF', 'XLS', 'XLSX'],
	},
	cdc_managed_youtube: {
		name: 'YouTube Video (Managed)',
		aliases: ['YouTube Video', 'YouTube'],
	},
	default: {
		fields: DEFAULT_FIELDS,
	},
	// community guide types
	cdc_cg_news: {
		name: 'News and Announcements',
		fields: {
			date: 'cdc_last_reviewed_date_dt',
		}
	},
	cdc_cg_finding: {
		name: 'Systematic Review',
		fields: {
			date: 'cdc_last_reviewed_date_dt',
		}
	},
	cdc_cg_resource: {
		name: 'Resource',
		fields: {
			date: 'cdc_last_reviewed_date_dt',
		}
	},
	cdc_cg_story: {
		name: 'In Action Story',
		fields: {
			date: 'cdc_last_reviewed_date_dt',
		}
	},
	cdc_cg_tool: {
		name: 'Tool',
		fields: {
			date: 'cdc_last_reviewed_date_dt',
		}
	},
};

/**
 * List of the content fields we care about in WCMS (many internal fields are meaningless here)
 *   key: system name of the WCMS field
 *   name: system friendly name of WCMS field
 *   aliases: possibly aliases the field is also known as
 *   solr: the solr field names for the WCMS field
 */
const CONTENT_FIELD_CONFIGS = {
	// Title
	post_title: {
		name: 'Post Title',
		type: 'text',
		aliases: [],
		solr: 'title_txt',
	},
	cdc_short_title: {
		name: 'Short Title',
		type: 'text',
		aliases: [],
		solr: 'cdc_short_title_str',
	},
	cdc_subtitle: {
		name: 'Subtitle',
		type: 'text',
		aliases: [],
		solr: 'cdc_subtitle_str',
	},
	cdc_file_name: {
		name: 'File Name',
		type: 'text',
		aliases: ['filename'],
		solr: 'cdc_file_name_str',
	},
	cdc_mobile_nav_title: {
		name: 'Mobile Page Title (Override)',
		type: 'text',
		aliases: ['Mobile title'],
		solr: 'cdc_mobile_nav_title_str',
	},

	// CONTENT
	post_content: {
		name: 'Post Content',
		type: 'html',
		aliases: ['content'],
		// NOTE: We don't use full page content in this app, only excerpts
		solr: 'excerpt_txt',
	},
	cdc_internal_description: {
		name: 'Link Descriptor Text',
		type: 'html',
		aliases: ['Link Text'],
		solr: 'excerpt_txt',
	},
	cdc_external_description: {
		name: 'Meta Description',
		type: 'textarea_small',
		aliases: ['Description'],
		solr: 'excerpt_txt',
	},
	cdc_keywords: {
		name: 'Meta Keywords',
		type: 'text',
		aliases: ['Keywords'],
		solr: 'cdc_keywords_str',
	},
	cdc_custom_page_html: {
		name: 'Custom Page HTML',
		type: 'html',
		aliases: ['page html', 'custom html'],
		solr: 'cdc_custom_page_html_str',
	},

	// URL
	public_url: {
		name: 'Public URL',
		type: 'text',
		aliases: ['permalink'],
		solr: 'permalink',
	},

	// Images
	cdc_related_image: {
		name: 'Iconic Image',
		type: 'url',
		aliases: ['image'],
		solr: 'cdc_related_image_str',
	},
	cdc_related_image_2: {
		name: 'Alternate Iconic Image',
		type: 'url',
		aliases: ['alt image'],
		solr: 'cdc_related_image_2_str',
	},
	cdc_alternative_file_formats: {
		name: 'Alternative File Format ',
		type: 'group',
		aliases: [],
		solr: 'cdc_alternative_file_formats_str',
	},
	cdc_alternative_languages: {
		name: 'Alternative Language ',
		type: 'group',
		aliases: [],
		solr: 'cdc_alternative_languages_str',
	},
	cdc_related_pages: {
		name: 'Related Pages ',
		type: 'group',
		aliases: [],
		solr: 'cdc_related_pages_str',
	},

	// Date fields
	cdc_article_date: {
		name: 'Article Date',
		type: 'date',
		aliases: [],
		solr: 'cdc_article_date_dt',
	},
	cdc_last_reviewed_date: {
		name: 'Review Date',
		type: 'date',
		aliases: [],
		solr: 'cdc_last_reviewed_date_dt',
	},
	cdc_last_modified_date: {
		name: 'Update Date',
		type: 'date',
		aliases: [],
		solr: 'cdc_last_modified_date_dt',
	},
	cdc_ratification_date: {
		name: 'Ratification Date',
		type: 'date',
		aliases: [],
		solr: 'cdc_ratification_date_dt',
	},
	cdc_release_date: {
		name: 'Release Date',
		type: 'date',
		aliases: [],
		solr: 'cdc_release_date_dt',
	},
	// core WP Post date
	cdc_post_date: {
		name: 'Created Date',
		type: 'date',
		aliases: ['date', 'post_date'],
		solr: 'date',
	},
	cdc_publish_date: {
		name: 'Publish Date',
		type: 'date',
		aliases: [],
		solr: 'cdc_publish_date_dt',
	},

	// Taxonomies
	cdc_folksonomy: {
		name: 'Folksonomy',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_folksonomy_str',
	},
	cdc_maintained_by: {
		name: 'Maintained By',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_maintained_by_str',
	},
	cdc_regional: {
		name: 'Regions',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_regional_str',
	},
	cdc_topic: {
		name: 'Topics',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_topic_str',
	},
	cdc_audience: {
		name: 'Audience',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_audience_str',
	},
	cdc_content_source: {
		name: 'Content Source',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_content_source_str',
	},
	cdc_document_type: {
		name: 'Document Type',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_document_type_str',
	},

	// NEW Taxonomy
	cdc_topics: {
		name: 'Topics',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_topics_taxonomy_str',
		solrParents: 'cdc_topics_taxonomy_parents_str',
	},
	cdc_populations: {
		name: 'People and Groups',
		type: 'taxonomy',
		aliases: ['Populations'],
		solr: 'cdc_populations_taxonomy_str',
		solrParents: 'cdc_populations_taxonomy_parents_str',
	},
	cdc_audiences: {
		name: 'Audiences',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_audiences_taxonomy_str',
		solrParents: 'cdc_audiences_taxonomy_parents_str',
	},
	cdc_settings: {
		name: 'Settings',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_settings_taxonomy_str',
		solrParents: 'cdc_settings_taxonomy_parents_str',
	},
	cdc_categories: {
		name: 'Categories',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_categories_taxonomy_str',
		solrParents: 'cdc_categories_taxonomy_parents_str',
	},
	cdc_geographic_scope: {
		name: 'Geographic Scope',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_geographic_scope_taxonomy_str',
		solrParents: 'cdc_geographic_scope_taxonomy_parents_str',
	},
	cdc_geolocation: {
		name: 'Geolocation',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_geolocation_taxonomy_str',
		solrParents: 'cdc_geolocation_taxonomy_parents_str',
	},
	cdc_subaudiences: {
		name: 'Sub Audiences',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_subaudiences_taxonomy_str',
		solrParents: 'cdc_subaudiences_taxonomy_parents_str',
	},
	cdc_entities: {
		name: 'Entities',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_entities_taxonomy_str',
		solrParents: 'cdc_entities_taxonomy_parents_str',
	},
	cdc_sector: {
		name: 'Sector',
		type: 'taxonomy',
		aliases: [],
		solr: 'cdc_sector_taxonomy_str',
		solrParents: 'cdc_sector_taxonomy_parents_str',
	},

	// Other
	cdc_soc_fp_options: {
		name: 'Facebook and Pinterest Options',
		type: 'group',
		aliases: [],
		solr: 'cdc_soc_fp_options_str',
	},
	cdc_home_page_feature: {
		name: 'Feature Area',
		type: 'url',
		aliases: [],
		solr: 'cdc_home_page_feature_str',
	},
	cdc_feature_style: {
		name: 'Feature Style',
		type: 'select',
		aliases: [],
		solr: 'cdc_feature_style_str',
	},
	cdc_sys_lang: {
		name: 'Language',
		type: 'select',
		aliases: [],
		solr: 'cdc_sys_lang_str',
	},
	cdc_leftnav_render_ssi_override: {
		name: 'Left Nav Render SSI Override Settings',
		type: 'group',
		aliases: [],
		solr: 'cdc_leftnav_render_ssi_override_str',
	},
	cdc_page_type: {
		name: 'Page Type',
		type: 'text',
		aliases: [],
		solr: 'cdc_page_type_str',
	},
};

// Takes WidgetOptions and figures out each post field => solr field to query, per content type
class SolrContentConfig {
	contentTypeConfigs = [];
	types = [];
	fields = [];
	filters = {};
	taxonomies = {};

	/**
	 * This is the constructor - accepts the base WidgetOptions object to figure out
	 *  - What are the solr fileds for each post field (title, image, date, content)
	 *
	 * @param {WidgetOpions} widgetOptions
	 */
	constructor(widgetOptions) {
		let allSolrFields = [];

		utils.toArray(widgetOptions.contentTypes).forEach((contentType) => {
			// options post type
			let postType = contentType.post_type || contentType;
			let postTypeName = contentType.name || postType;
			// options post fields config - custom choices optional
			let contentTypeConfig = this.getSolrContentConfig(postType, postTypeName);

			// no repeats
			if (-1 < this.types.indexOf(contentTypeConfig.type)) {
				return;
			}

			// allow widget options to override post field / solr  mapping
			CORE_POST_FIELDS.forEach(postField => {
				if (widgetOptions.contentFields[postField] && widgetOptions.contentFields[postField].field) {
					let currentSolrField = contentTypeConfig.fields[postField];
					let newSolrField = this.getSolrField( widgetOptions.contentFields[postField].field );
					if ( newSolrField && newSolrField !== currentSolrField ) {
						contentTypeConfig.fields[postField] = [].concat(newSolrField).concat(currentSolrField);
					}
				}
			});

			this.types.push(contentTypeConfig.name);

			// for each content type, keep record of the related solr fields
			for ( let postField in contentTypeConfig.fields ) {
				// if post field is a core field
				if (-1 < CORE_POST_FIELDS.indexOf( postField ) ||
					( widgetOptions.contentFields && widgetOptions.contentFields[postField] &&
					widgetOptions.contentFields[postField].display )
				) {
					allSolrFields = allSolrFields.concat( utils.toArray( contentTypeConfig.fields[postField] ) );
				}
			}

			this.contentTypeConfigs.push(contentTypeConfig);
		});

		// figure out mapping between filter id => filter solr field
		utils.toArray(widgetOptions.filters).forEach((filter) => {
			let filterId = utils.cleanString(filter.value || filter.id).toLowerCase();
			let solrField = this.getSolrField(filterId);
			if (filterId && solrField) {
				this.filters[filterId] = solrField;
				allSolrFields.push(solrField);
			}
		});

		this.fields = utils.unique(allSolrFields);
	}

	/**
	 * Takes an input content type (like 'cdc_page') and returns an object
	 * that has all default mappings between WCMS field ids and SOLR field ids
	 * see CONTENT_TYPE_CONFIGS above
	 *
	 * @param {string} contentType
	 * @returns {object}
	 */
	getSolrContentConfig(contentType, contentTypeName) {
		let contentTypeConfig = false;
		contentType = utils.cleanString(contentType).toLowerCase();

		// find a match and build the fields from defaults and custom fields per type
		for (let key in CONTENT_TYPE_CONFIGS) {
			let options = [key];
			if (CONTENT_TYPE_CONFIGS[key].aliases) {
				options = options.concat(CONTENT_TYPE_CONFIGS[key].aliases);
			}
			options = options.join('|').toLowerCase().split('|');
			if (-1 < options.indexOf(contentType)) {
				contentTypeConfig = Object.assign({ type: key }, CONTENT_TYPE_CONFIGS[key]);
				contentTypeConfig.fields = Object.assign({}, DEFAULT_FIELDS, contentTypeConfig.fields);
				break;
			}
		}
		// default for unconfigured post types
		if ( ! contentTypeConfig ) {
			config.debug && console.info('cc: content_type not found: ', contentType);
			contentTypeConfig = {
				key: contentType,
				name: utils.cleanString( contentTypeName || contentType ),
				fields: DEFAULT_FIELDS,
			};
		}
		return contentTypeConfig;
	}


	/**
	 * Given a WCMS field (id, name, label, post_meta key), return the matching
	 * object from CONTENT_FIELD_CONFIGS above containing config info about the field
	 *
	 * @param {string} sourceField
	 * @returns {object} Object from CONTENT_FIELD_CONFIGS
	 */
	getSolrFieldConfig(sourceField) {
		let solrFieldConfig = false;
		sourceField = utils.cleanString(sourceField).toLowerCase();

		// @TODO: handle custom taxonomies and unfound fields
		for (let key in CONTENT_FIELD_CONFIGS) {
			let contentField = CONTENT_FIELD_CONFIGS[key];
			let aliases = utils.toArray(contentField.aliases);
			if (
				key === sourceField ||
				(String(key).match(/^cdc_/) && sourceField === key.replace(/^cdc_/, '')) ||
				sourceField === String(contentField.name).toLowerCase() ||
				-1 < aliases.indexOf(sourceField) ||
				contentField.solr === sourceField
			) {
				solrFieldConfig = contentField;
				break;
			}
		}
		return solrFieldConfig;
	}

	/**
	 * Given a WCMS field (label, name, post_meta key), return the SOLR field
	 *
	 * @param {string} sourceField Expect a sourceField
	 * @returns {string}
	 */
	getSolrField(sourceField) {
		sourceField = utils.cleanString(sourceField).toLowerCase();
		let solrContentFieldConfig = this.getSolrFieldConfig(sourceField);
		if (solrContentFieldConfig) {
			return solrContentFieldConfig.solr;
		}
		// if we get here, it's probably a custom taxonomy
		return sourceField + '_str';
	}

	/**
	 * Given a WCMS field, return the "parents" SOLR field for the WMCS field.
	 * The parents field is specific to taxonomies - it contains the id's of all parent terms
	 *
	 * @param {string} sourceField
	 * @returns {string|false} Solr Parents field, if found - false if not
	 */
	getSolrParentsField(sourceField) {
		sourceField = utils.cleanString(sourceField).toLowerCase();
		let solrParentsField = false;
		let solrContentFieldConfig = this.getSolrFieldConfig(sourceField);
		if (solrContentFieldConfig && solrContentFieldConfig.solrParents) {
			solrParentsField = solrContentFieldConfig.solrParents;
		} else if (! solrContentFieldConfig) {
			// if we don't have a config, it's likely a local taxonomy
			solrParentsField = sourceField + '_parents_str';
		}
		return solrParentsField;
	}

	/**
	 * From a built config, give this a raw solr result, and get all post field values returned
	 * based on the content type of the document
	 *
	 * @param {object} solrResult Raw SOLR result document
	 * @returns {object|false} Built result if possible, or false if not
	 */
	getFieldsFromSolrResult(solrResult) {
		solrResult = Object.assign({}, solrResult);
		let id = utils.cleanString(solrResult.id);
		let type = utils.cleanString(solrResult.type);
		let typeTxt = utils.cleanString(solrResult.type_txt);
		let result = {
			id: id,
			type: type,
		};
		// find appropriate contentTypeConfig for result type
		let contentTypeConfig = this.contentTypeConfigs.find((item) => item.name === typeTxt);
		if (!contentTypeConfig) {
			console.error('cc: type config not found for result: ', type);
			return false;
		}
		Object.keys(contentTypeConfig.fields).forEach((postField) => {
			let solrFields = utils.toArray(contentTypeConfig.fields[postField]);
			result[postField] = '';
			if ('altFormats' === postField) {
				result[postField] = this.getAltFormatsFromSolrResult(solrResult);
			} else if ('altLanguages' === postField) {
				result[postField] = this.getAltLanguagesFromSolrResult(solrResult);
			} else if ('relatedContent' === postField) {
				result[postField] = this.getRelatedContentFromSolrResult(solrResult);
			} else {
				solrFields.forEach((solrField) => {
					if (!result[postField] && solrResult[solrField]) {
						result[postField] = utils.cleanString(solrResult[solrField]);
					}
				});
			}
		});
		// account for filters
		result.filters = {};
		for( const [key, value] of Object.entries(this.filters)){
			if ( solrResult[value] ){
				result.filters[key] = utils.toArray(solrResult[value]).map(termId => String(termId));
			}
		}
		return result;
	}

	/**
	 * Once built, given a post field (image, title, date, content, url) get an
	 * array of possible SOLR fields associated (usually just one)
	 * @param {string} postField post field to get
	 * @return {array} Array of matched solrFields
	 */
	getSolrFieldsForPostField(postField) {
		let solrFields = [];
		this.contentTypeConfigs.forEach((contentTypeConfig) => {
			if (contentTypeConfig.fields[postField]) {
				solrFields = solrFields.concat(contentTypeConfig.fields[postField]);
			}
		});
		return utils.unique(solrFields);
	}

	/**
	 * Specifically grabs altFormats from a solr result
	 * @param {object} solrResult Solr result
	 * @return {array} Array of alt formats for result
	 */
	getAltFormatsFromSolrResult(solrResult) {
		let altFormats = [];
		if ( !utils.isObject(solrResult)) {
			return altFormats;
		}
		let linksCount = 0;
		// determine number of links
		Object.keys(solrResult).forEach(resultField => {
			let match = resultField.match(/^cdc_alternative_file_formats_(\d+)/);
			if (match && 1 < match.length) {
				linksCount = Math.max(linksCount, utils.toNumber(match[1]) + 1);
			};
		});
		for (let index = 0; index < linksCount; index++) {
			let url = utils.cleanString( solrResult['cdc_alternative_file_formats_' + index + '_cdc_alternative_file_format_str'] || '' );
			let text = utils.cleanString( solrResult['cdc_alternative_file_formats_' + index + '_cdc_alt_file_format_link_text_str'] || '' );
			if (url) {
				altFormats.push({ url, text });
			}
		}
		return altFormats;
	}

	/**
	 * Specifically grabs related content from a solr result
	 * @param {*object} solrResult
	 * @returns {array} Array of related content docs for result
	 */
	getRelatedContentFromSolrResult(solrResult){
		let relatedContent = [];
		if(!utils.isObject(solrResult)) {
			return relatedContent;
		}
		let linksCount = 0;
		//determine number of links;
		Object.keys(solrResult).forEach(resultField => {
			let match = resultField.match(/^cdc_related_pages_(\d+)/);
			if( match && 1 < match.length) {
				linksCount = Math.max(linksCount, utils.toNumber(match[1]) + 1);
			};
		});
		for (let index = 0; index < linksCount; index++){
			let url = utils.cleanString(solrResult['cdc_related_pages_' + index + '_url_str'] || '');
			let text = utils.cleanString(solrResult['cdc_related_pages_' + index + '_title_str'] || '');
			if (url) {
				relatedContent.push({url, text});
			}
		}
		return relatedContent;
	}
	/**
	 * Specifically grabs altLanguages from a solr result
	 * @param {object} solrResult Solr result
	 * @return {array} Array of alt language docs for result
	 */
	getAltLanguagesFromSolrResult(solrResult) {
		let altLanguages = [];
		if ( !utils.isObject(solrResult)) {
			return altLanguages;
		}
		let linksCount = 0;
		// determine number of links
		Object.keys(solrResult).forEach(resultField => {
			let match = resultField.match(/^cdc_alternative_languages_(\d+)/);
			if (match && 1 < match.length) {
				linksCount = Math.max(linksCount, utils.toNumber(match[1]) + 1);
			};
		});
		for (let index = 0; index < linksCount; index++) {
			let url = utils.cleanString( solrResult['cdc_alternative_languages_' + index + '_cdc_alternative_language_str'] || '' );
			let text = utils.cleanString( solrResult['cdc_alternative_languages_' + index + '_cdc_alt_language_link_text_str'] || '' );
			let lang = utils.cleanString( solrResult['cdc_alternative_languages_' + index + '_cdc_alt_language_mp_translate_str'] || '' );
			if (url && (lang || text)) {
				altLanguages.push({ url, text, lang });
			}
		}
		return altLanguages;
	}

}

export default SolrContentConfig;
